import React from 'react';
import NotifyTenantPage from './modules/NotifyTenantPage';
import HowItWorksPage from './modules/HowItWorksPage';
import SubmissionPage from './modules/SubmissionPage';
import ConfirmationPage from './modules/ConfirmationPage';
import DifferentValueThanLeaseWarning from '@rental-manager/pages/Onboarding/RentalProtectionDifferentValueWarning/component';
import Modal from '@common/components/Modal';
import userPersonas from '@common/constants/userPersonas';
import { ExtendedRentAmountChangeModel, IStyledTextModel } from '@rental-manager/models/Types';

type EditRentAmountPageProps = {
  today: Date;
  endDateForPayments: string;
  amount: number;
  rentAmountChangeRequest: ExtendedRentAmountChangeModel;
  onSubmit: (...args: any) => any;
  continueToDashboard: ({ tenantFirstName }: { tenantFirstName: string }) => void;
  onClose: () => void;
  trackValidationError: (...args: any) => any;
  rentTermsKey: string;
  tenantFirstName: string;
  isRentalProtectionVariation: boolean;
  listingAlias: string;
  rentalProtectionDisclaimers: IStyledTextModel[];
};

type EditRentAmountPageState = {
  currentPage: number;
  editingRequest: boolean;
  selectedDate: string;
  prevRentAmount: string;
  nextAllowedChangeDate: string;
  maxMonthsForRentAmountChange: number;
};

class EditRentAmountPage extends React.Component<EditRentAmountPageProps, EditRentAmountPageState> {
  static defaultProps = {
    today: new Date(),
    endDateForPayments: '',
    onSubmit: () => {},
    continueToDashboard: () => {},
    onClose: () => {},
    trackValidationError: () => {},
  };

  static NotifyTenantPage = 1;
  static RentAmountDifferentThanLeaseWaringPage = 2;
  static HowItWorksPage = 3;
  static SubmissionPage = 4;
  static ConfirmationPage = 5;

  constructor(props: EditRentAmountPageProps) {
    super(props);
    const { amount, rentAmountChangeRequest } = props;
    const { newAmount, effectiveDueDate, maxMonthsForRentAmountChange, nextAllowedChangeDate } =
      rentAmountChangeRequest;

    this.state = {
      currentPage: effectiveDueDate ? EditRentAmountPage.SubmissionPage : EditRentAmountPage.NotifyTenantPage,
      editingRequest: Boolean(effectiveDueDate),
      selectedDate: effectiveDueDate || nextAllowedChangeDate,
      prevRentAmount: String(newAmount || amount),
      nextAllowedChangeDate,
      maxMonthsForRentAmountChange,
    };
  }

  getPreviousPage = (currentPage: number): number => currentPage - 1;

  getNextPage = (currentPage: number): number => currentPage + 1;

  togglePrevPage = (): void => {
    const { currentPage } = this.state;

    if (currentPage === EditRentAmountPage.NotifyTenantPage) {
      this.onClose();
    } else if (!this.props.isRentalProtectionVariation && currentPage === EditRentAmountPage.HowItWorksPage) {
      this.setState({ currentPage: currentPage - 2 });
    } else {
      this.setState({ currentPage: this.getPreviousPage(currentPage) });
    }
  };

  toggleNextPage = (): void => {
    const { currentPage } = this.state;
    // If isRentalProtectionVariation = false, skip the DifferentValueThanLeaseWarning screen
    if (!this.props.isRentalProtectionVariation && currentPage === EditRentAmountPage.NotifyTenantPage) {
      this.setState({ currentPage: currentPage + 2 });
    } else {
      this.setState({ currentPage: this.getNextPage(currentPage) });
    }
  };

  onSubmit = async (payload) => {
    this.setState({ selectedDate: payload.effectiveDate });
    payload.rentTermsKey = this.props.rentTermsKey;
    return this.props.onSubmit(payload);
  };

  continueToDashboard = () => {
    const { tenantFirstName } = this.props;
    return this.props.continueToDashboard({ tenantFirstName });
  };

  onClose = (): void => {
    this.props.onClose();
  };

  renderCurrentPage = () => {
    const { today, isRentalProtectionVariation, listingAlias, rentalProtectionDisclaimers } = this.props;
    const { currentPage, selectedDate, prevRentAmount, nextAllowedChangeDate, maxMonthsForRentAmountChange } =
      this.state;

    if (currentPage === EditRentAmountPage.NotifyTenantPage) {
      return <NotifyTenantPage togglePrevPage={this.togglePrevPage} toggleNextPage={this.toggleNextPage} />;
    } else if (
      isRentalProtectionVariation &&
      currentPage === EditRentAmountPage.RentAmountDifferentThanLeaseWaringPage
    ) {
      return (
        <DifferentValueThanLeaseWarning
          onContinue={this.toggleNextPage}
          onClose={this.onClose}
          onBack={this.togglePrevPage}
          fromDashboard
          rentalProtectionDisclaimers={rentalProtectionDisclaimers}
          listingAlias={listingAlias}
        />
      );
    } else if (currentPage === EditRentAmountPage.HowItWorksPage) {
      return <HowItWorksPage toggleNextPage={this.toggleNextPage} />;
    } else if (currentPage === EditRentAmountPage.SubmissionPage) {
      return (
        <SubmissionPage
          today={today}
          prevRentAmount={prevRentAmount}
          nextAllowedChangeDate={nextAllowedChangeDate}
          maxMonthsForRentAmountChange={maxMonthsForRentAmountChange}
          toggleNextPage={this.toggleNextPage}
          onSubmit={this.onSubmit}
          onClose={this.onClose}
          trackValidationError={this.props.trackValidationError}
        />
      );
    } else if (currentPage === EditRentAmountPage.ConfirmationPage) {
      return <ConfirmationPage selectedDate={selectedDate} continueToDashboard={this.continueToDashboard} />;
    }
    return null;
  };

  render(): React.ReactNode {
    const { editingRequest, currentPage } = this.state;

    // Don't show back button for first and last page, or if editing request.
    const hideBackButton =
      currentPage === EditRentAmountPage.NotifyTenantPage ||
      currentPage === EditRentAmountPage.ConfirmationPage ||
      editingRequest;

    return (
      <Modal
        persona={userPersonas.LANDLORD}
        header="PAYMENT SETTINGS"
        onRequestClose={this.onClose}
        onClickBack={hideBackButton ? null : this.togglePrevPage}
        dataAutoTestId="landlord-edit-rent-amount"
      >
        {this.renderCurrentPage()}
      </Modal>
    );
  }
}

export default EditRentAmountPage;
